// Service for fetching NASA FIRMS active fire detections.
// A free MAP_KEY is required on the backend:
// https://firms.modaps.eosdis.nasa.gov/api/map_key
//
// The React app calls the Herding Helpers API proxy at /herding_api/firms
// because NASA FIRMS does not send CORS headers.

const FIRMS_PROXY_URL = '/herding_api/firms';

// Default source: VIIRS NOAA-20 near-real-time, 375 m resolution.
const DEFAULT_SOURCE = 'VIIRS_NOAA20_NRT';

// Cache by bounding box + day range. Fire detections update every ~15 minutes,
// so a short TTL keeps the map fresh without hammering the API.
const cache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getCache(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

function setCache(key, data) {
  cache.set(key, { data, timestamp: Date.now() });
}

function isValidCoord(value) {
  if (value == null) return false;
  const num = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(num);
}

function toGeoJSON(firmsRecords) {
  return {
    type: 'FeatureCollection',
    features: (firmsRecords || [])
      .filter((r) => isValidCoord(r.latitude) && isValidCoord(r.longitude))
      .map((r) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(r.longitude), parseFloat(r.latitude)],
        },
        properties: {
          brightness: r.brightness ?? null,
          brightT31: r.bright_t31 ?? null,
          frp: r.frp ?? null, // fire radiative power in MW
          confidence: r.confidence ?? null,
          satellite: r.satellite ?? null,
          instrument: r.instrument ?? null,
          acquiredAt: r.acq_date && r.acq_time
            ? `${r.acq_date} ${r.acq_time.slice(0, 2)}:${r.acq_time.slice(2, 4)} UTC`
            : null,
          daynight: r.daynight ?? null,
        },
      })),
  };
}

async function readJsonOrText(response) {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    // eslint-disable-next-line no-console
    console.warn('[FIRMS] non-JSON response body:', text);
    const snippet = text.slice(0, 200).replace(/\s+/g, ' ');
    throw new Error(`Expected JSON but got: ${snippet}`);
  }
}

/**
 * Fetch active fire detections from NASA FIRMS for a bounding box.
 *
 * @param {Object} options
 * @param {number} options.west   - Western boundary (longitude)
 * @param {number} options.south  - Southern boundary (latitude)
 * @param {number} options.east   - Eastern boundary (longitude)
 * @param {number} options.north  - Northern boundary (latitude)
 * @param {number} [options.dayRange=1] - Number of days to look back (1-10)
 * @param {string} [options.source='VIIRS_NOAA20_NRT'] - FIRMS data source
 * @param {string} [options.apiKey] - Optional API key (defaults to env var)
 * @returns {Promise<GeoJSON FeatureCollection>}
 */
export async function fetchFIRMSFiresGeoJSON({
  west,
  south,
  east,
  north,
  dayRange = 1,
  source = DEFAULT_SOURCE,
} = {}) {
  const params = new URLSearchParams({
    west: String(west),
    south: String(south),
    east: String(east),
    north: String(north),
    source,
    dayRange: String(dayRange),
  });
  const url = `${FIRMS_PROXY_URL}?${params.toString()}`;
  const cacheKey = url;

  const cached = getCache(cacheKey);
  if (cached) return cached;

  // eslint-disable-next-line no-console
  console.log('[FIRMS] fetching:', url);

  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
  });

  const payload = await readJsonOrText(response);

  if (!response.ok) {
    // eslint-disable-next-line no-console
    console.warn('[FIRMS] proxy error payload:', payload);
    const message = payload?.error || JSON.stringify(payload);
    throw new Error(`FIRMS proxy HTTP ${response.status}: ${message}`);
  }

  // eslint-disable-next-line no-console
  const count = payload?.type === 'FeatureCollection'
    ? payload.features?.length
    : (Array.isArray(payload) ? payload.length : 'n/a');
  console.log('[FIRMS] records received:', count);

  // Backend now returns GeoJSON FeatureCollection directly.
  const geojson = payload?.type === 'FeatureCollection' ? payload : toGeoJSON(payload);
  setCache(cacheKey, geojson);
  return geojson;
}

/**
 * Build a bounding box around a center point with a given degree padding.
 */
export function bboxAround(center, padding = 2.0) {
  return {
    west: center.lng - padding,
    south: center.lat - padding,
    east: center.lng + padding,
    north: center.lat + padding,
  };
}

/**
 * Clear the in-memory FIRMS cache.
 */
export function clearFIRMSCache() {
  cache.clear();
}

const firmsService = {
  fetchFIRMSFiresGeoJSON,
  bboxAround,
  clearFIRMSCache,
};

export default firmsService;
